import { Link } from "react-router";
import { Alerta, Badge, BotonWhatsapp, Button, DataList, Dialog, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { Fiduciante } from "@/lib/api";
import { ETIQUETA_PORTAL, ETIQUETA_TIPO, formatDocumento, formatParticipacion, OPCIONES_TIPO, TONO_PORTAL } from "@/lib/participacion";
import { formatTelefono } from "@/lib/telefonos";
import { PasswordTemporalParaCompartir } from "@/tabs/configuracion/PasswordTemporal";
import { useFiduciantesTab } from "./useFiduciantesTab";

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string | null }) {
  return (
    <div className="contents">
      <dt className="text-texto-suave">{etiqueta}</dt>
      <dd className="break-words">{valor || "—"}</dd>
    </div>
  );
}

/** "60 % (clase A y B)" — las clases sólo se ven en la ficha. */
function textoParticipacion(f: Fiduciante): string {
  const clases = f.clases_vigentes.length > 0 ? ` (clase ${f.clases_vigentes.join(" y ")})` : "";
  return `${formatParticipacion(f.porcentaje_vigente)}${clases}`;
}

export function FiduciantesTab() {
  const t = useFiduciantesTab();
  const d = t.dialogo;

  // Lo demás (tipo, documento, contacto, clases) está en la ficha, tocando el nombre; del portal sólo se avisa lo
  // pendiente (Mati, 2026-10-19).
  const columnas = [
    {
      clave: "nombre",
      titulo: "Nombre",
      render: (f: Fiduciante) => (
        <Row espacio={2} envolver>
          <Button variante="fantasma" tamanio="sm" className="-ml-3" aria-label={`Ver la ficha de ${f.nombre}`} onClick={() => t.abrirFicha(f)}>
            {f.nombre}
          </Button>
          {TONO_PORTAL[f.estado_portal] === "alerta" && <Badge tono="alerta">{ETIQUETA_PORTAL[f.estado_portal]}</Badge>}
        </Row>
      ),
    },
    {
      clave: "participacion",
      titulo: "Participación",
      render: (f: Fiduciante) => <span className="tabular-nums">{formatParticipacion(f.porcentaje_vigente)}</span>,
    },
  ];

  const acciones = (f: Fiduciante) => (
    <>
      {f.usuario_id === null && (
        <Button variante="secundario" tamanio="sm" aria-label={`Dar acceso al portal a ${f.nombre}`} onClick={() => void t.darAcceso(f)}>
          Dar acceso
        </Button>
      )}
      <BotonWhatsapp telefono={f.telefono} nombre={f.nombre} />
    </>
  );

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Fiduciantes</h1>
        <Row espacio={2} envolver>
          {t.puedeVerComo && (
            <Button variante="secundario" cargando={t.entrandoVerComo} onClick={() => void t.verComo()}>
              Ver como fiduciante
            </Button>
          )}
          {t.esAdmin && <Button onClick={t.abrirNuevo}>Nuevo fiduciante</Button>}
        </Row>
      </Row>
      <Alerta texto={t.errorAcceso} />
      <Alerta texto={t.errorVerComo} />
      <Alerta
        tono="alerta"
        texto={t.avisoSinDistribucion}
        accion={<Link to="/participacion" className="font-medium underline underline-offset-2">Cargala en Participación</Link>}
      />
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList
          etiqueta="Fiduciantes"
          columnas={columnas}
          filas={t.fiduciantes}
          claveFila={(f) => f.id}
          acciones={t.esAdmin ? acciones : undefined}
          vacio="Todavía no hay fiduciantes cargados."
        />
      )}

      <Dialog abierto={d?.tipo === "ficha"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "ficha" ? d.fiduciante.nombre : "Fiduciante"}>
        {d?.tipo === "ficha" && (
          <Stack espacio={4}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-chico">
              <Dato etiqueta="Tipo" valor={ETIQUETA_TIPO[d.fiduciante.tipo]} />
              <Dato etiqueta="Documento" valor={d.fiduciante.documento && formatDocumento(d.fiduciante.documento)} />
              <Dato etiqueta="Participación" valor={textoParticipacion(d.fiduciante)} />
              <Dato etiqueta="Email" valor={d.fiduciante.email} />
              <Dato etiqueta="Teléfono" valor={d.fiduciante.telefono && formatTelefono(d.fiduciante.telefono)} />
              <Dato etiqueta="Domicilio" valor={d.fiduciante.domicilio} />
              <Dato etiqueta="Portal" valor={ETIQUETA_PORTAL[d.fiduciante.estado_portal]} />
              <Dato etiqueta="Notas" valor={d.fiduciante.notas} />
            </dl>
            {t.esAdmin && (
              <Row justificar="fin">
                <Button variante="secundario" onClick={() => d.tipo === "ficha" && t.abrirEditar(d.fiduciante)}>
                  Editar
                </Button>
              </Row>
            )}
          </Stack>
        )}
      </Dialog>

      <Dialog
        abierto={d?.tipo === "form"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo={d?.tipo === "form" && d.fiduciante ? "Editar fiduciante" : "Nuevo fiduciante"}
      >
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            <Select etiqueta="Tipo" opciones={OPCIONES_TIPO} valor={t.form.tipo || undefined} onCambio={(v) => t.cambiar("tipo", v)} error={t.errores.tipo} />
            <TextField etiqueta="Nombre" value={t.form.nombre} onChange={(e) => t.cambiar("nombre", e.target.value)} error={t.errores.nombre} />
            <TextField
              etiqueta="Documento"
              ayuda="DNI, CUIT o CUIL"
              inputMode="numeric"
              value={t.form.documento}
              onChange={(e) => t.cambiar("documento", e.target.value)}
              error={t.errores.documento}
            />
            <TextField etiqueta="Email" type="email" inputMode="email" value={t.form.email} onChange={(e) => t.cambiar("email", e.target.value)} error={t.errores.email} />
            <TextField etiqueta="Teléfono" type="tel" value={t.form.telefono} onChange={(e) => t.cambiar("telefono", e.target.value)} />
            <TextField etiqueta="Domicilio" value={t.form.domicilio} onChange={(e) => t.cambiar("domicilio", e.target.value)} />
            <TextField
              etiqueta="Notas"
              ayuda="La ve también el propio fiduciante"
              value={t.form.notas}
              onChange={(e) => t.cambiar("notas", e.target.value)}
            />
            <Alerta texto={t.errorGuardar} />
            <Row justificar="entre" envolver espacio={2}>
              {d?.tipo === "form" && d.fiduciante ? (
                <Button variante="fantasma" tamanio="sm" onClick={() => d.fiduciante && t.abrirBorrar(d.fiduciante)}>
                  Borrar fiduciante
                </Button>
              ) : (
                <span />
              )}
              <Row espacio={2}>
                <Button variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardando}>
                  Guardar
                </Button>
              </Row>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar fiduciante">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>¿Borrar a {d.fiduciante.nombre}? No se puede deshacer.</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "email"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Dar acceso al portal">
        {d?.tipo === "email" && (
          <form onSubmit={(e) => void t.confirmarEmail(e)} noValidate>
            <Stack espacio={4}>
              <p>Para entrar al portal, {d.fiduciante.nombre} necesita un email. Queda guardado en su ficha.</p>
              <TextField etiqueta="Email" type="email" inputMode="email" value={t.email} onChange={(e) => t.cambiarEmail(e.target.value)} error={t.errorEmail} />
              <Alerta texto={t.errorGuardarEmail} />
              <Row justificar="fin" espacio={2}>
                <Button variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardandoEmail}>
                  Dar acceso
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "acceso"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Acceso al portal">
        {d?.tipo === "acceso" && (
          <Stack espacio={4}>
            {d.temporal ? (
              <PasswordTemporalParaCompartir datos={d.temporal} />
            ) : (
              <p>{d.nombre} ya tenía usuario: puede entrar con su contraseña de siempre.</p>
            )}
            <Button onClick={t.cerrar}>Listo</Button>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
